/**
 * Google Maps Platform cost model for the admin dashboard — turns the
 * proxy's per-action month counters (google-proxy.php usage-stats `month`
 * block) into estimated dollars.
 *
 * Prices are USD per 1,000 billable calls at the 0–100K volume tier, and
 * free caps are the per-SKU monthly free calls Google switched to when the
 * shared $200 credit ended (March 2025): Essentials 10,000 / Pro 5,000 /
 * Enterprise (incl. + Atmosphere) 1,000. The SKU per action follows from
 * the exact field masks the proxy sends. ESTIMATES ONLY — Cloud Console →
 * Billing → Reports is the invoice truth; re-check prices against
 * https://developers.google.com/maps/billing-and-pricing/pricing if a
 * number here disagrees with a bill.
 */
export const ACTION_SKUS = {
  'place-details-ext': {
    // extended=true mask includes editorialSummary → Enterprise + Atmosphere
    label: 'Place details — rich',
    tier: 'Enterprise+Atmosphere',
    price: 25,
    free: 1000,
  },
  'places-text-search': {
    label: 'Text search',
    tier: 'Pro',
    price: 32,
    free: 5000,
  },
  'places-nearby': {
    label: 'Nearby search',
    tier: 'Pro',
    price: 32,
    free: 5000,
  },
  'place-details': {
    // basic masks (displayName/formattedAddress/location/types) → Pro
    label: 'Place details — basic',
    tier: 'Pro',
    price: 17,
    free: 5000,
  },
  photo: {
    label: 'Place photo',
    tier: 'Essentials',
    price: 7,
    free: 10000,
  },
  geocode: {
    label: 'Geocoding',
    tier: 'Essentials',
    price: 5,
    free: 10000,
  },
  'places-autocomplete': {
    label: 'Autocomplete',
    tier: 'Essentials',
    price: 2.83,
    free: 10000,
  },
  'static-map': {
    label: 'Static map',
    tier: 'Essentials',
    price: 2,
    free: 10000,
  },
  directions: {
    // Routes API Compute Routes Basic (polyline/distance/duration mask)
    label: 'Route (On the way)',
    tier: 'Essentials',
    price: 5,
    free: 10000,
  },
  // expand-maps-link, usage-stats, test: no Google API behind them — free.
};

/**
 * Rows + totals for the month cost table. billable = requests − cache hits
 * (per action); cost only accrues past the SKU's free cap. Sorted most
 * expensive first, then by how close to the free cap. Unknown actions
 * (legacy keys, junk) are skipped rather than priced wrong.
 */
export function estimateMonthCosts(month) {
  const byAction = month?.by_action || {};
  const byActionHits = month?.by_action_hits || {};
  const rows = [];
  let totalCost = 0;
  let totalBillable = 0;

  for (const [action, sku] of Object.entries(ACTION_SKUS)) {
    const requests = byAction[action] || 0;
    if (!requests) continue;
    const billable = Math.max(0, requests - (byActionHits[action] || 0));
    const overage = Math.max(0, billable - sku.free);
    const cost = (overage / 1000) * sku.price;
    rows.push({
      action,
      label: sku.label,
      tier: sku.tier,
      price: sku.price,
      free: sku.free,
      requests,
      billable,
      capUsed: billable / sku.free,
      overage,
      cost,
    });
    totalBillable += billable;
    totalCost += cost;
  }

  rows.sort((a, b) => b.cost - a.cost || b.capUsed - a.capUsed || b.billable - a.billable);
  return { rows, totalBillable, totalCost };
}

/** "$1.28", "$0.00" — always cents, always a leading dollar sign. */
export function formatUsd(amount) {
  return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
}
